import {
    Fieldset,
    fromTheme,
    h3,
    nativeOnlyStyles,
    Form as PrimitiveForm,
    ScrollView,
    styled,
    Text,
    View,
    webOnlyStyles
} from 'lib_ui-primitives';

export const Loading = styled(h3)`
    margin: ${fromTheme('textMargin')};
`;

// Flex shrink is needed for scrolling of forms inside of dialogs when the
// form is taller than the dialog.
let _StyledForm = styled(PrimitiveForm).attrs({ name: 'styled-form' })`
    background-color: transparent;
    flex-grow: ${props => (props.heightGreedy ? 1 : 0)};
    width: 100%;
    flex-shrink: 1;
    max-height: ${({ theme: { height, native } }) => (native ? 9999 : height)}px;
    position: relative;
`;
_StyledForm = webOnlyStyles(_StyledForm)`
    overflow-y: ${({ scrollWholeForm }) => (scrollWholeForm ? 'auto' : 'hidden')};
    overflow-x: hidden;
`;
export const StyledForm = _StyledForm;
StyledForm.displayName = 'StyledForm';

// max-width here keeps the footer from flowing off the screen on
// small mobile devices.
let _FormFooter = styled(View).attrs({ name: 'form-footer' })`
    flex-basis: auto;
    flex-grow: 0;
    flex-shrink: 0;
    flex-wrap: wrap;
    justify-content: flex-end;
    border-top-width: ${({ theme }) => (theme.mobile ? '1px' : '0')};
    border-top-color: ${fromTheme('borderColor')};
    max-width: ${fromTheme('width')};
`;

_FormFooter = webOnlyStyles(_FormFooter)`
    border-top-style: solid;
    box-shadow: ${fromTheme('form', 'buttonBar', 'boxShadow')};
`;
_FormFooter.displayName = 'FormFooter';
export const FormFooter = _FormFooter;

export const ErrorSection = styled(View).attrs({ name: 'error-section' })`
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
`;
export const ErrorText = styled(h3).attrs({ name: 'error-text' })`
    font-size: ${fromTheme('fontSize')};
    color: ${fromTheme('colorScheme', 'error')};
    margin: ${fromTheme('textMargin')};
`;
ErrorText.displayName = 'ErrorText';

export const ButtonBar = styled(View).attrs({ name: 'button-bar' })`
    flex-basis: auto;
    flex-grow: ${({ theme }) => (theme.selectedTheme === 'gl2025' ? 1 : 0)};
    flex-shrink: 0;
    flex-direction: row;
    justify-content: ${fromTheme('form', 'buttonBar', 'justifyContent')};
    min-height: ${fromTheme('form', 'buttonBar', 'minHeight')};
    margin: ${fromTheme('form', 'buttonBar', 'margin')};
`;
ButtonBar.displayName = 'ButtonBar';

export const WrappingFieldset = styled(Fieldset).attrs({ name: 'wrapping-fieldset' })`
    flex-wrap: wrap;
`;
WrappingFieldset.displayName = 'WrappingFieldset';

let _GreedyOtherElements = styled(View).attrs({ name: 'greedy-other-elements' })`
    flex-basis: 1px;
    flex-grow: 1;
    flex-direction: column;
    min-height: ${({ theme, theme: { height }, scrollWholeForm }) =>
        theme.selectedTheme === 'gl2025' ? 0 : (scrollWholeForm ? height / 2 : 0) + 'px'};
`;

_GreedyOtherElements = nativeOnlyStyles(_GreedyOtherElements)`
    min-height: ${({ theme }) => (theme.selectedTheme === 'gl2025' && theme.mobile ? 80 : 0)};
`;

_GreedyOtherElements = webOnlyStyles(_GreedyOtherElements)`
    overflow-y: ${({ theme, scrollWholeForm }) => (theme.selectedTheme === 'gl2025' ? 'hidden' : scrollWholeForm ? 'visible' : 'hidden')};
`;

export const GreedyOtherElements = _GreedyOtherElements;
GreedyOtherElements.displayName = 'GreedyOtherElements';

export const InactiveOverlay = styled(View).attrs({ name: 'inactive-overlay' })`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${fromTheme('form', 'inactive', 'overlay')};
    z-index: 10;
`;
InactiveOverlay.displayName = 'InactiveOverlay';

export const InactiveBanner = styled(View).attrs({ name: 'inactive-banner' })`
    display: flex;
    background-color: ${fromTheme('form', 'inactive', 'backgroundColor')};
    padding: ${fromTheme('form', 'inactive', 'bannerPadding')};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    z-index: 11;
`;

export const InactiveBannerTitle = styled(h3).attrs({ name: 'inactive-banner-title' })`
    ${fromTheme('form', 'inactive', 'titleFont')}
    color: ${fromTheme('form', 'inactive', 'titleColor')};
    margin: 0;
    margin-bottom: ${fromTheme('form', 'inactive', 'titleMarginBottom')};
`;

export const InactiveBannerText = styled(Text).attrs({ name: 'inactive-banner-text' })`
    ${fromTheme('form', 'inactive', 'textFont')};
    color: ${fromTheme('form', 'inactive', 'textColor')};
`;

export const FormHeader = styled(View).attrs({ name: 'form-header' })`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-left: ${fromTheme('viewPaddingMore')};
    padding-right: ${fromTheme('viewPaddingMore')};
    flex-shrink: 0;
    border-bottom-width: ${({ theme }) => (theme.mobile ? '1px' : '0')};
    border-bottom-color: ${fromTheme('borderColor')};
`;

FormHeader.displayName = 'FormHeader';

let _FormBody = styled(View).attrs({ name: 'form-body' })`
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
`;
_FormBody = webOnlyStyles(_FormBody)`
    &::-webkit-scrollbar {
        width: ${fromTheme('tabs', 'scrollbar', 'width')};
        height: ${fromTheme('tabs', 'scrollbar', 'height')};
    }
    &::-webkit-scrollbar-track {
        background: ${fromTheme('scrollbarTrack')};
        border-radius: ${fromTheme('tabs', 'scrollbar', 'borderRadius')};
        margin-top: ${fromTheme('tabs', 'scrollbar', 'marginTop')};
        margin-bottom: ${fromTheme('tabs', 'scrollbar', 'marginBottom')};
    }
    &::-webkit-scrollbar-thumb {
        background: ${fromTheme('scrollbarThumb')};
        border-radius: ${fromTheme('tabs', 'scrollbar', 'borderRadius')};
    }
    &::-webkit-scrollbar-thumb:hover {
        background: ${fromTheme('scrollbarThumbHover')};
    }
`;
export const FormBody = _FormBody;
FormBody.displayName = 'FormBody';

// Native-only scrollable form body. On web, form-body gets overflow-y: auto via
// SingleRecordFormLayout CSS. On native, we need a ScrollView component instead.
// Used for GL2025 mobile forms where form content can exceed the viewport.
export const ScrollableFormBody = styled(ScrollView).attrs({ name: 'form-body' })`
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    flex-direction: column;
`;
ScrollableFormBody.displayName = 'ScrollableFormBody';

let _HeaderButtons = styled(View).attrs({ name: 'header-buttons' })`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

_HeaderButtons = webOnlyStyles(_HeaderButtons)`
    & > button > svg {
        height: ${fromTheme('form', 'buttonBar', 'buttonSizeInPx')};
        width: ${fromTheme('form', 'buttonBar', 'buttonSizeInPx')};
    }
`;
_HeaderButtons = nativeOnlyStyles(_HeaderButtons)`
    & > button > svg {
        height: ${fromTheme('form', 'buttonBar', 'buttonSizeInPx')};
        width: ${fromTheme('form', 'buttonBar', 'buttonSizeInPx')};
    }
`;

_HeaderButtons.displayName = 'FormHeaderButtons';

export const HeaderButtons = _HeaderButtons;

let _AssetNo = styled(Text).attrs({ name: 'asset-no' })`
    ${fromTheme('form', 'buttonBar', 'mobile', 'assetNoFont')}
    color: ${fromTheme('form', 'buttonBar', 'mobile', 'assetNoColor')};
`;

_AssetNo = webOnlyStyles(_AssetNo)`
    display: block;
`;

export const AssetNo = _AssetNo;
AssetNo.displayName = 'AssetNo';

let _MobileTitle = styled(Text).attrs({ name: 'mobile-title' })`
    ${fromTheme('form', 'buttonBar', 'mobile', 'titleFont')}
    color: ${fromTheme('form', 'buttonBar', 'mobile', 'titleColor')};
    margin: 0;
`;

_MobileTitle = webOnlyStyles(_MobileTitle)`
    display: block;
`;

export const MobileTitle = _MobileTitle;
MobileTitle.displayName = 'MobileTitle';
